import { isProduction } from 'config/constants';
import history from 'config/history';
import { addQueryParams, removeQueryParams } from 'helpers/url';

export const getStepIndex = (steps, stepId) => {
  const index = steps.findIndex(({ id }) => id === stepId);
  return index === -1 ? null : index;
};

/**
 * [handleStepContinue] - helper for handling completing a step and finishing or continuing through a wizard
 *
 * @param {func} onContinue - function (must return promise) that runs before navigating to the next step
 * @param {func} onFinish - function (must return promise) that runs before navigating to the next step
 * @param {object[]} steps - wizard steps
 * @param {number} stepIndex - current position in wizard steps
 */

export const handleStepContinue = ({ onContinue, onFinish, steps, stepIndex }) =>
  onContinue()
    .then(() => {
      const nextStep = steps[stepIndex + 1];
      if (nextStep) {
        history.push({
          ...history.location,
          pathname: nextStep.path
        });
        return Promise.resolve();
      }

      // When the last step finishes, we want to display a loading indicator while we run the 'onFinish' logic,
      // otherwise there is an awkward lag on the last step. Ensure the query param is removed once onFinish resolves
      if (onFinish) {
        addQueryParams({ loading: true });
        const removeLoadingState = () => removeQueryParams('loading');
        return onFinish({ onError: removeLoadingState });
      }

      console.warn('No steps remaining and no "onFinish" is not defined');
      return Promise.resolve();
    })
    // Silently handle error for now
    .catch(e => {
      if (!isProduction) console.warn('[onContinue] failed', e);
    });

/**
 * [handleStepContinue] - helper for handling completing a step and finishing or continuing through a wizard
 *
 * @param {func} onContinue - function (must return promise) that runs before navigating to the next step
 * @param {func} [onPrevious] - function to run prior to attempting to navigate to previous path
 * @param {object[]} steps - wizard steps
 * @param {number} stepIndex - current position in wizard steps
 */

export const handleStepPrevious = ({
  onPrevious: onPreviousParam,
  steps,
  stepIndex,
  overviewPath
}) => {
  // It's common that onPrevious accidentally passes the event handler... this ensures
  // a function is passed
  const onPrevious =
    typeof onPreviousParam === 'function' ? onPreviousParam : () => Promise.resolve();

  return onPrevious().then(() => {
    const prevStep = steps[stepIndex - 1];
    if (prevStep) {
      history.push({
        ...history.location,
        pathname: prevStep.path
      });
      return Promise.resolve();
    }

    if (steps.length > 1) {
      history.push({
        ...history.location,
        pathname: overviewPath
      });
      return Promise.resolve();
    }

    console.warn('No previous steps');
    return Promise.resolve();
  });
};

/**
 * [getNextStepIndex] - finds the wizard step index for passed config
 *
 * @param {object[]} steps
 * @param {boolean}  steps.isComplete - if a step is not complete, then we don't let the user move ahead in the wizard
 *  if it is also required
 * @param {boolean}  steps.isRequired - if a step is required, then it must be completed before moving to the next step
 * @param {object}  options
 * @param {boolean} options.[required]   - returns the first required step in the steps array that isn't complete
 * @param {boolean} options.[incomplete] - returns the first step in the steps array that is not complete
 *
 * @returns {number} returns -1 if no step found
 */

export const getNextStepIndex = (steps, options = {}) => {
  const { required, incomplete } = options;

  if (required) {
    return steps.findIndex(
      ({ isComplete, isRequired }) => isComplete === false && isRequired === true
    );
  }

  if (incomplete) {
    return steps.findIndex(({ isComplete }) => isComplete === false);
  }

  return -1; // simulate findIndex with no matches
};
